import { motion, AnimatePresence } from 'framer-motion';
import { Lock, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../actions/Button';

interface AuthRequiredModalProps {
  isOpen: boolean;
  onClose?: () => void;
  action: 'save' | 'publish' | 'limit';
}

export function AuthRequiredModal({
  isOpen,
  onClose,
  action,
}: AuthRequiredModalProps) {
  const navigate = useNavigate();

  const handleLogin = () => {
    sessionStorage.setItem('auth-return-action', action);
    navigate('/login');
  };

  const handleSignup = () => {
    sessionStorage.setItem('auth-return-action', action);
    navigate('/register');
  };

  const dismissable = action !== 'limit';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={dismissable ? onClose : undefined}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-background-card rounded-xl shadow-xl z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-lg font-semibold text-text-primary">
                  {action === 'limit' ? 'Free Limit Reached' : 'Account Required'}
                </h2>
              </div>
              {dismissable && onClose && (
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full hover:bg-background flex items-center justify-center text-text-secondary"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-text-secondary">
                {action === 'save' ? (
                  <>
                    To save your invite and access it later, you'll need to create an account or log in.
                    Don't worry, your design will be waiting for you.
                  </>
                ) : action === 'publish' ? (
                  <>
                    To publish your invite and share it with guests, you'll need to create an account or log in.
                    Your design will be saved automatically.
                  </>
                ) : (
                  <>
                    You've been exploring Invitely for a while — it's time to create a free account to keep building.
                    Your current design will be saved to your account automatically.
                  </>
                )}
              </p>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t border-border bg-background">
              <Button
                variant="secondary"
                onClick={handleLogin}
                fullWidth
              >
                Log In
              </Button>
              <Button
                variant="primary"
                onClick={handleSignup}
                fullWidth
              >
                Create Account
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
